import {
  ParticipantCreatePayload,
  ParticipantUpdatePayload,
  SearchParticipantsPayload,
} from "@/domain/participant.interface";
import { ParticipantsService } from "@/services/participants.service";
import { useMutation, useQuery } from "@tanstack/react-query";

export const useParticipantById = (id: string) => {
  const {
    data,
    isLoading: isLoadingParticipant,
    isError: isErrorParticipant,
    refetch: refetchParticipant,
  } = useQuery({
    queryKey: ["coyn-participant-by-id", id],
    queryFn: () => ParticipantsService.findById(id),
  });

  return {
    data,
    isLoadingParticipant,
    isErrorParticipant,
    refetchParticipant,
  };
};

export const useParticipantSearch = (params?: SearchParticipantsPayload) => {
  const {
    data,
    isLoading: isLoadingParticipants,
    refetch: refetchParticipants,
    isFetching: isFetchingParticipants,
  } = useQuery({
    queryKey: [
      "coyn-participant-search",
      params?.sortField,
      params?.sortOrder,
      params?.pageNumber,
      params?.pageSize,
      params?.search,
      params?.programIds,
      params?.orgUnitId,
      params?.status,
      params?.notEnrolled,
    ],
    queryFn: () => ParticipantsService.search(params || {}),
    enabled: true,
  });
  return {
    data,
    isLoadingParticipants,
    refetchParticipants,
    isFetchingParticipants,
  };
};

export const useCreateParticipant = () => {
  return useMutation({
    mutationFn: (data: ParticipantCreatePayload) =>
      ParticipantsService.create(data),
  });
};

export const useUpdateParticipant = () => {
  return useMutation({
    mutationFn: (data: { id: string; payload: ParticipantUpdatePayload }) => {
      return ParticipantsService.update({
        id: data.id,
        payload: data.payload,
      });
    },
    onMutate: () => {
      // Optimistic update can be added here if needed
    },
    onSuccess: () => {
      // Success handling
    },
    onError: () => {},
  });
};

export const useGetProgramOrgUnits = (
  participantId: string,
  userId: string,
  editing?: boolean,
) => {
  const {
    data: programOrgUnits,
    isLoading: isLoadingProgramOrgUnits,
    isError: isErrorProgramOrgUnits,
    refetch: refetchProgramOrgUnits,
    isFetching: isFetchingProgramOrgUnits,
  } = useQuery({
    queryKey: ["coyn-participant-programs", participantId, userId],
    queryFn: () =>
      ParticipantsService.getProgramOrgUnits(participantId, userId, editing),
  });

  return {
    programOrgUnits,
    isLoadingProgramOrgUnits,
    isErrorProgramOrgUnits,
    refetchProgramOrgUnits,
    isFetchingProgramOrgUnits,
  };
};

export const useSoftDeleteParticipant = () => {
  return useMutation({
    mutationFn: (id: string) => ParticipantsService.softDelete(id),
  });
};

export const useGlobalSearch = (search: string, userId: string) => {
  const {
    data: participants,
    isLoading: isLoadingParticipants,
    isError: isErrorParticipants,
    refetch: refetchParticipants,
    isFetching: isFetchingParticipants,
  } = useQuery({
    queryKey: ["coyn-participant-search-global", search, userId],
    queryFn: () => ParticipantsService.globalSearch({ search, userId }),
  });

  return {
    participants,
    isLoadingParticipants,
    isErrorParticipants,
    refetchParticipants,
    isFetchingParticipants,
  };
};
